import { useState } from "react";
import { Copy, Check, UserRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const plain = (runs) => (runs || []).map((r) => r.t).join("").trim();

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }
}

export function CopyButton({ text, testid, label = "Copy message" }) {
  const [done, setDone] = useState(false);
  const run = async () => {
    if (await copyText(text)) {
      setDone(true);
      toast.success("Copied — personalise names, date and time before sending");
      setTimeout(() => setDone(false), 2000);
    } else {
      toast.error("Couldn't copy automatically — select the text and copy it manually");
    }
  };
  return (
    <button onClick={run} data-testid={testid}
            className="inline-flex items-center gap-1.5 min-h-[44px] rounded-full bg-[#0A1F44] text-white text-sm font-semibold px-4 py-2 hover:bg-[#0A1F44]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961]">
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {done ? "Copied" : label}
    </button>
  );
}

export function RunsText({ runs, className = "" }) {
  return (
    <div className={`text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words ${className}`}>
      {(runs || []).map((r, i) => {
        const cls = `${r.b ? "font-semibold" : ""} ${r.i ? "italic" : ""}`;
        return r.u
          ? <a key={i} href={r.u} target="_blank" rel="noopener noreferrer" className={`${cls} text-[#0A1F44] underline decoration-[#C9A961] underline-offset-2 break-all`}>{r.t}</a>
          : <span key={i} className={cls}>{r.t}</span>;
      })}
    </div>
  );
}

export function ForYou({ runs, testid }) {
  if (!plain(runs)) return null;
  return (
    <aside className="rounded-xl border border-dashed border-[#C9A961] bg-[#FCF6DF]/70 p-3" data-testid={testid} aria-label="For you — internal coaching note">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#8A6D1F] font-semibold mb-1">
        <UserRound className="w-3.5 h-3.5" /> For you · not part of the message
      </div>
      <RunsText runs={runs} className="text-[13px] text-slate-700" />
    </aside>
  );
}

export function ReviewFlag({ items, testid }) {
  if (!items?.length) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={testid} role="note">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>Confirm the next appointment with your coach — this step's wording ({items.map((x) => `“${x}”`).join(", ")}) is waiting for review in the master guide.</span>
    </div>
  );
}

export default function MessageCard({ msg, testid, showLabel = true }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 border-b border-slate-100 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{showLabel ? msg.label : "Message to send"}</span>
          <CopyButton text={plain(msg.body)} testid={`${testid}-copy`} />
        </div>
        <div className="px-3 sm:px-4 py-3" data-testid={`${testid}-body`}><RunsText runs={msg.body} /></div>
        <div className="px-3 sm:px-4 pb-2 text-[11px] text-slate-500">Personalise names, date and time before sending.</div>
      </div>
      <ReviewFlag items={msg.review} testid={`${testid}-review`} />
      <ForYou runs={msg.note} testid={`${testid}-note`} />
    </div>
  );
}
